/**
 * Avatar Frame Definitions
 *
 * Decorative borders around the user's avatar. Each frame is a CSS-class
 * identifier; styling lives in avatarFrames.css. See STORE_SYSTEM.md §4.2.
 */

import type { Rarity, Acquisition } from './rarity';

export interface AvatarFrame {
  id: string;
  name: string;
  description: string;
  /** Buyable price; for earned frames this is the box dust/refund value. */
  price: number;
  rarity: Rarity;
  /** Omit for buyable ('shop'); 'earned' = challenge/box only. */
  acquisition?: Acquisition;
  /** CSS class applied on the AvatarFrame wrapper. */
  cssClass: string;
  /** Single accent color used for swatches and fallback rendering. */
  swatchColor: string;
}

export const AVATAR_FRAMES: AvatarFrame[] = [
  {
    id: 'bronze',
    name: 'Bronze',
    description: 'A simple bronze ring. Entry-level prestige.',
    price: 60,
    rarity: 'common',
    cssClass: 'avatar-frame-bronze',
    swatchColor: '#cd7f32',
  },
  {
    id: 'silver',
    name: 'Silver',
    description: 'Cool silver border with a soft sheen.',
    price: 90,
    rarity: 'common',
    cssClass: 'avatar-frame-silver',
    swatchColor: '#c0c0c0',
  },
  {
    id: 'gold',
    name: 'Gold',
    description: 'The classic golden halo.',
    price: 120,
    rarity: 'rare',
    cssClass: 'avatar-frame-gold',
    swatchColor: '#facc15',
  },
  {
    id: 'diamond',
    name: 'Diamond',
    description: 'Crystalline rainbow ring. Catches every light.',
    price: 175,
    rarity: 'rare',
    cssClass: 'avatar-frame-diamond',
    swatchColor: '#67e8f9',
  },
  // ── Earned-only (animated tier): challenge rewards / box drops ──────────
  {
    id: 'royal',
    name: 'Royal',
    description: 'Animated gold-and-purple pulse.',
    price: 1000,
    rarity: 'epic',
    acquisition: 'earned',
    cssClass: 'avatar-frame-royal',
    swatchColor: '#a855f7',
  },
  {
    id: 'inferno',
    name: 'Inferno',
    description: 'Animated flames around your avatar.',
    price: 1500,
    rarity: 'legendary',
    acquisition: 'earned',
    cssClass: 'avatar-frame-inferno',
    swatchColor: '#ef4444',
  },
];

export function getAvatarFrame(id: string | undefined): AvatarFrame | null {
  if (!id) return null;
  return AVATAR_FRAMES.find(f => f.id === id) ?? null;
}
